import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { useInboxReplyContext } from "app/store/InboxReplyContext";
import { SCROLL_DELAY_MS } from "common/consts/scrollDelay";
import { useSelectedMessageId } from "hooks/use-selected-message-id";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import { useRootStore } from "store";
// import { Icon } from "components/icon/Icon";
import { Loader } from "components/loader/Loader";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";
import { filterAndSortMessages } from "helpers/inbox/messageAndThreadHelpers";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import { usePermission } from "hooks/use-permissions";
import { Attachment } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import CreateThreadDesktop from "views/inbox/components/Inbox/main/createThread/desktop";
import Message from "views/inbox/components/Inbox/main/message";
import ThreadActionBar from "views/inbox/components/Inbox/main/threadPreview/ThreadActionBar";
import DiscardMessageModal from "views/inbox/DiscardMessageModal";
import ThreadSubscribers from "../ThreadSubscribers";

import "../threadPreview.scss";

export default function ThreadPreviewDesktop() {
  const customerId = useRequiredCustomerId();
  const { inboxType, threadId } = useParams<{
    inboxType?: string;
    threadId?: string;
  }>();

  const selectedMessageId = useSelectedMessageId();
  const {
    currentThread,
    isLoadingThread,
    loadingThread,
    fetchThread,
    sendMessage,
    failedToGetThread
  } = useRootStore((state) => state.inbox);
  const navigate = useNavigate();
  // const location = useLocation();
  const { workingCustomer: customer } = useRootStore(
    (state) => state.customers
  );

  const { internalUsers, isLoading: isLoadingCustomer } = customer;

  const { user, archive } = useRootStore((state) => state);
  const { sendFileToArchive } = archive;

  const userInfoId = user.userInfo ? user.userInfo.id : "";
  const isInternal = usePermission("isInternal");

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const {
    isReplying,
    setIsReplying,
    isDirty,
    clearDirty,
    resetFormState,
    formContextId,
    setFormContextId
  } = useInboxReplyContext();

  const [isSending, setIsSending] = useState(false);
  // const [copied, setCopied] = useState(false);
  const [showConfirmDiscardModal, setShowConfirmDiscardModal] = useState(false);

  const {
    setThreadStatus,
    archiveThread,
    unarchiveThread,
    updateThreadCategory
  } = useRootStore((state) => state.inbox);

  const subscribers = currentThread?.subscribers || [];

  // Reset form state when navigating to a different thread (context changes)
  // This persists on resize (formContextId stays same in context)
  useEffect(() => {
    const currentThreadId = currentThread?.threadId;

    if (currentThreadId && formContextId !== currentThreadId) {
      setIsReplying(false);
      resetFormState(currentThread?.subscribers, currentThread?.title);
      setFormContextId(currentThreadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentThread?.threadId,
    currentThread?.title,
    formContextId,
    setFormContextId,
    setIsReplying,
    resetFormState
  ]);

  // Don't render reply form until context is ready (form has been reset for this thread)
  const isFormReady = formContextId === currentThread?.threadId;

  const subscriberIds = subscribers.map((subscriber) => {
    return subscriber.id;
  });

  const absentSubscribers = internalUsers.filter(
    (internalUser) =>
      internalUser.id !== userInfoId &&
      subscriberIds.includes(internalUser.id) &&
      internalUser.outOfOfficeMessage
  );

  const subscriber = currentThread?.subscribers.find(
    (sub) => sub.id === userInfoId
  );

  const { messages: visibleMessages } = useMemo(() => {
    return filterAndSortMessages(currentThread?.messages ?? [], isInternal);
  }, [currentThread?.messages, isInternal]);

  const handleThreadListAction = (action: string) => {
    if (!threadId || !action || !currentThread) {
      return;
    }

    if (currentThread.unread) {
      return;
    }
    setThreadStatus(customerId, threadId, action);
  };

  //   Copy thread link to clipboard - currently commented out, will be added in future releases
  // const handleCopy = async () => {
  //   if (!currentThread) {
  //     return;
  //   }
  //   try {
  //     const baseThreadUrl = `${window.location.origin}${location.pathname}`;

  //     await navigator.clipboard.writeText(baseThreadUrl);
  //     setCopied(true);

  //     setTimeout(() => setCopied(false), 2000);
  //   } catch (err) {
  //     console.error("Failed to copy!", err);
  //   }
  // };

  const handleOnReply = async (
    body: string,
    users: string[],
    replayThreadId: string,
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    try {
      loadingThread();

      const result = await sendFileToArchive(
        filesToUpload,
        customerId,
        "Inbox",
        setFailedWrapper
      );

      if (!Array.isArray(result)) {
        if (threadId && customerId) {
          fetchThread(customerId, threadId);
        }
        return;
      }

      const uploadedAttachments = result.map((file) => ({
        id: file.id,
        name: file.name,
        type: file.type
      }));

      const allAttachments = [...attachments, ...uploadedAttachments];
      sendMessage(
        user.userInfo ? user.userInfo.id : "",
        body,
        "",
        replayThreadId,
        users,
        customerId,
        allAttachments
      );
    } catch (error) {
      console.error("Error while replying:", error);
      failedToGetThread();
    }
  };

  const handleOnSend = async (
    title: string,
    body: string,
    users: string[],
    attachments: Attachment[],
    filesToUpload: DocumentUploadFile[]
  ) => {
    if (!currentThread || !threadId) {
      return;
    }
    setIsSending(true);

    await handleOnReply(body, users, threadId, attachments, filesToUpload)
      .then(() => {
        clearDirty();
        resetFormState(currentThread?.subscribers, currentThread?.title);
        setIsReplying(false);
        setIsSending(false);
      })
      .catch(() => {
        setIsSending(false);
      });
  };

  const handleDiscardClicked = () => {
    if (isDirty) {
      setShowConfirmDiscardModal(true);
      return;
    }
    resetFormState(currentThread?.subscribers, currentThread?.title);
    setIsReplying(false);
  };

  const handleConfirmDiscard = () => {
    clearDirty();
    resetFormState(currentThread?.subscribers, currentThread?.title);
    setShowConfirmDiscardModal(false);
    setIsReplying(false);
  };

  const handleArchiveThread = async () => {
    if (threadId && customerId) {
      archiveThread(customerId, encodeURIComponent(threadId));
      navigate(`/customers/${customerId}/inbox/${inboxType || "my"}`);
    }
  };

  const handleUndoArchiveThread = async () => {
    if (threadId && customerId) {
      unarchiveThread(customerId, encodeURIComponent(threadId));
      navigate(`/customers/${customerId}/inbox/${inboxType || "my"}`);
    }
  };

  const handleCategoryChange = async (category: number) => {
    if (threadId && customerId) {
      await updateThreadCategory(
        customerId,
        encodeURIComponent(threadId),
        category
      );
    }
  };

  useEffect(() => {
    if (currentThread?.isArchived && !isInternal) {
      navigate(`/customers/${customerId}/inbox/${inboxType || "my"}`);
    }
  }, [currentThread?.isArchived, navigate, customerId, inboxType, isInternal]);

  useEffect(() => {
    const extractThreadUUID = currentThread?.threadId ? currentThread.threadId.split("/").pop() : "";
    const isThreadLoaded = extractThreadUUID === threadId;
    if (threadId && customerId && !isThreadLoaded) {
      fetchThread(customerId, threadId, navigate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, customerId]);

  useEffect(() => {
    if (isLoadingThread) {
      return;
    }

    if (!currentThread?.messages?.length) {
      return;
    }

    if (!selectedMessageId) {
      return;
    }

    const messageExists = currentThread.messages.some(
      (msg) => msg.messageId === selectedMessageId
    );

    if (!messageExists) {
      return;
    }

    const element = document.getElementById(`${selectedMessageId}`);
    if (element) {
      setTimeout(() => {
        const threadWrapper = element.closest(".thread-wrapper");
        if (threadWrapper) {
          const elementRect = element.getBoundingClientRect();
          const containerRect = threadWrapper.getBoundingClientRect();
          const scrollTop =
            threadWrapper.scrollTop + (elementRect.top - containerRect.top);

          threadWrapper.scrollTo({
            top: scrollTop,
            behavior: "smooth"
          });
        } else {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, SCROLL_DELAY_MS);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingThread, currentThread?.threadId, selectedMessageId]);

  if (!threadId) {
    return (
      <p className="placeholder">
        Välj en konversation för att visa dess innehåll.
      </p>
    );
  }

  return (
    <div className="w-100">
      {(isLoadingThread || isLoadingCustomer) && (
        <div className="d-flex align-items-center justify-content-center">
          <Loader className="mr-3" /> Laddar konversationen...
        </div>
      )}

      {!isLoadingThread && !currentThread && !isLoadingCustomer && (
        <div>
          Vi kunde inte ladda konversationen! Kontakta support för att få hjälp.
        </div>
      )}

      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}

      {currentThread && (
        <>
          <div className="thread-header">
            <div className="d-flex mr-3 align-items-center mb-3">
              <h4 className="thread-title fw-semi-bold d-inline text-truncate mr-5">
                {currentThread?.title || ""}
              </h4>
              {/* Copy thread link to clipboard - currently commented out, will be added in future releases */}
              {/* <button
                onClick={handleCopy}
                className="copy-link-btn fw-semi-bold text-nowrap cursor-pointer"
              >
                <Icon className="mr-2" name="copy" />
                {copied ? "Link copied!" : "Copy link"}
              </button> */}
            </div>

            <ThreadActionBar
              onReply={() => setIsReplying(true)}
              onMarkAsUnread={() => handleThreadListAction("unread")}
              showMarkAsUnread={!currentThread.unread && !!subscriber}
              subscriber={!!subscriber}
              onCategoryChange={handleCategoryChange}
              onArchiveClick={handleArchiveThread}
              onUndoArchiveClick={handleUndoArchiveThread}
              isReplying={isReplying}
            />
          </div>
          <div className="thread-wrapper">
            {/* Show all Thread subscribers section */}
            <ThreadSubscribers />
            <hr className="hr-divider" />

            {/* Show text of absent subscribers */}
            {!isReplying && absentSubscribers.length > 0 && (
              <>
                <div className="mt-2">
                  <label className="mb-3">Frånvaromeddelanden</label>

                  {absentSubscribers.map((u) => {
                    return (
                      <div className="mt-0 out-of-office-wrapper" key={u.id}>
                        <b>
                          {u.givenName} {u.surName}
                        </b>
                        :
                        <div className="marginTop out-of-office-message">
                          {u.outOfOfficeMessage.split("\n").map((item, key) => {
                            return (
                              <span key={key}>
                                {item}
                                <br />
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <hr className="hr-divider hr-divider-sm" />
              </>
            )}

            {isSending && <Loader />}

            {isReplying && isFormReady && (
              <>
                {showConfirmDiscardModal && (
                  <DiscardMessageModal
                    onDismiss={() => setShowConfirmDiscardModal(false)}
                    onCancel={() => setShowConfirmDiscardModal(false)}
                    onConfirm={handleConfirmDiscard}
                  />
                )}
                <CreateThreadDesktop
                  isSending={isSending}
                  defaultRecipients={currentThread.subscribers}
                  onSend={handleOnSend}
                  thread={currentThread}
                  onDiscardClicked={handleDiscardClicked}
                />
              </>
            )}

            {visibleMessages.map((message, index) => (
              <React.Fragment key={message.messageId}>
                <Message
                  key={message.messageId}
                  id={message.messageId}
                  message={message}
                  thread={currentThread}
                />
                {index !== visibleMessages.length - 1 && (
                  <hr className="hr-divider" />
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
